import { InfoToast } from '@services/utils/ToastUtils';
import { theme } from '@theme';
import { FC, useState } from 'react';
import styled from 'styled-components';
import { getNumberAmountText } from '../../../services/utils/FormatHelper';
import {
  HeaderTextProps,
  PrivateIncentiveDetailsProps,
} from '../../../types/PrivateIncentiveLookup';
import { Icon } from '../../style/Icon';
import { Typography } from '../../style/Typography';

const months = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May',
  'Jun.',
  'Jul.',
  'Aug.',
  'Sep.',
  'Oct.',
  'Nov.',
  'Dec.',
];

const PrivateIncentiveDetails: FC<PrivateIncentiveDetailsProps> = (props) => {
  const newMazdaCampaignStartdate = new Date(Date.UTC(2023, 7, 2));
  const isMazda = props.brand === 'mazd';
  const alternateModels = isMazda ? ['All Eligible Non-Primary Vehicles'] : props.alternateModals;
  const isMazdaJulAugCampaign = new Date(props.startDate) < newMazdaCampaignStartdate;
  const mazdaCampaignMessage = isMazdaJulAugCampaign
    ? 'Please see Mazda program bulletin for eligibility rules. The only eligible 2024 model is the CX-90 (gas).'
    : 'Please see Mazda program bulletin for eligibility rules.';
  const leaseBuyoutBrands = ['audi'];
  const isLeaseBuyoutBrand = leaseBuyoutBrands.indexOf(props.brand) > -1;
  const showLeaseBuyoutBrandsMessage = props.isLeaseBuyOut && isLeaseBuyoutBrand;
  const [listCount, SetListCount] = useState(0);

  const getStatusTag = () => {
    //compare only date. Exclude time.
    const dateToCompare = new Date(props.endDate);
    dateToCompare.setHours(0, 0, 0, 0);

    let today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateToCompare >= today) {
      return (
        <span className='activeSpan' data-testid='status-tag-active'>
          Active
        </span>
      );
    } else {
      return (
        <span className='expiredSpan' data-testid='status-tag-expired'>
          Expired
        </span>
      );
    }
  };

  function toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  const getModalTag = (
    header: HeaderTextProps,
    modals: string[],
    privateIncentiveAmount: string,
  ) => {
    if (modals?.length > 0 && privateIncentiveAmount) {
      let headerTag = (
        <div className='model-tag bold-sub-text'>
          <div>{header.column1}</div>
          <div>{header.column2}</div>
        </div>
      );

      let bodyTag = modals?.map((item, index) => {
        return (
          <div key={index}>
            <hr className='formatted no-margin' />
            <div className='model-tag'>
              <div>{item}</div>
              <div>${getNumberAmountText(parseInt(privateIncentiveAmount))}</div>
            </div>
          </div>
        );
      });

      return (
        <div>
          {headerTag}
          {bodyTag}
        </div>
      );
    } else {
      return <></>;
    }
  };

  const getPrimaryModalTag = (
    header: HeaderTextProps,
    modals: any[],
    privateIncentiveAmount: string,
  ) => {
    const slice = modals?.slice(listCount, listCount + 5);
    if (modals?.length > 0 && privateIncentiveAmount) {
      let headerTag = (
        <div className='model-tag bold-sub-text'>
          <div>{header.column1}</div>
          <div>{header.column2}</div>
        </div>
      );

      let bodyTag = slice.map((item, index) => {
        return (
          <div key={index}>
            <hr className='formatted no-margin' />
            <div className='model-tag'>
              <div>{item}</div>
              <div>${getNumberAmountText(parseInt(privateIncentiveAmount))}</div>
            </div>
          </div>
        );
      });

      return (
        <div className={'primary-modal'}>
          {headerTag}
          {bodyTag}
        </div>
      );
    } else {
      return <></>;
    }
  };

  const getFormattedDate = (date) => {
    if (date) {
      let dt = new Date(date);
      return `${months[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
    } else {
      return '';
    }
  };

  const handleCopyButtonClick = async () => {
    if (props?.redemptionCode) {
      await navigator.clipboard.writeText(props.redemptionCode);
      InfoToast('Code has been copied to the clipboard.');
    }
  };

  return (
    <WrapperPrivateIncentiveDetails type={props.type}>
      <div className={props.type == 'mobile' ? '' : 'row'}>
        <div className='col-md-4 private-inc-details row'>
          {props.type !== 'mobile' && (
            <div className='header-text-details'>Private Incentive Details</div>
          )}
          {props.type == 'mobile' && <div className='program-name'>{props.program}</div>}

          <div className='content-text'>
            {props.type !== 'mobile' && (
              <>
                <div>Last Name: {toTitleCase(props.lastName)}</div>
                <br />
                {props.vinCurrentlyOwned && (
                  <>
                    <div>
                      VIN Currently Owned:
                      <br />
                      {props.vinCurrentlyOwned}
                    </div>
                    <br />
                  </>
                )}
              </>
            )}

            <div>
              {getFormattedDate(props.startDate)} - {getFormattedDate(props.endDate)}{' '}
              {props.type !== 'mobile' && getStatusTag()}
            </div>
            {props.type !== 'mobile' && <div>Program: {props.program}</div>}
            <div>Bulletin: {props.bulletin}</div>
            {props.type !== 'mobile' && <hr className='formatted' />}
            <div className='bold-sub-text'>
              Redemption Code: {props.redemptionCode}{' '}
              <CopyButton onClick={handleCopyButtonClick} data-testid='copy-button'>
                <Icon type='copy' size={15} />
                Copy
              </CopyButton>
            </div>
            {props.primaryModalsIncentiveAmount &&
            props.primaryAltModalsIncentiveAmount &&
            props.primaryModalsIncentiveAmount === props.primaryAltModalsIncentiveAmount ? (
              <div className={'primary-modal'}>
                <hr className={'formatted'} />
                <div className={'model-tag'}>
                  <div>
                    <Typography size={theme.typography.h4} theme={theme}>
                      All Non-Excluded Vehicles
                    </Typography>
                  </div>
                  <div>${getNumberAmountText(parseInt(props.primaryModalsIncentiveAmount))}</div>
                </div>
                <hr className='formatted no-margin' />
              </div>
            ) : (
              <>
                <div>
                  {getPrimaryModalTag(
                    { column1: 'Primary Models', column2: 'Amount' },
                    props.primaryModals,
                    props.primaryModalsIncentiveAmount,
                  )}
                </div>
                <hr className='formatted no-margin end-primary-models' />
                <span>
                  Total vehicles:{' '}
                  <span className='bold-sub-text'>{props.primaryModals.length}</span>
                </span>
                {props.primaryModals.length > 5 && (
                  <ButtonWrapper>
                    <Button
                      isActive={listCount !== 0}
                      onClick={() => SetListCount(listCount - 5)}
                      disabled={listCount == 0}
                    >
                      <Icon type='angle-left' size={18} className='-icon' />
                    </Button>
                    <Button
                      isActive={listCount + 5 < props.primaryModals.length}
                      onClick={() => SetListCount(5 + listCount)}
                      disabled={listCount + 5 > props.primaryModals.length}
                    >
                      <Icon type='angle-right' size={18} className='-icon' />
                    </Button>
                  </ButtonWrapper>
                )}

                <div>
                  {getModalTag(
                    { column1: 'Alternate Models', column2: 'Amount' },
                    alternateModels,
                    props.primaryAltModalsIncentiveAmount,
                  )}
                </div>
              </>
            )}
            <div className='excluded-model'>
              <span className='bold-sub-text'>Excluded Models:</span> {props.excludedModals.trim()}
            </div>
            <br />
            {showLeaseBuyoutBrandsMessage && (
              <Typography size={theme.typography.h4} color={theme.palette.wetAsphalt} theme={theme}>
                {`Incentive amounts vary by model purchased through Reactivation Bonus campaigns. Please see program bulletin ${props.bulletin} for complete details.`}
              </Typography>
            )}
            {isMazda && (
              <MazdaCampaignMessage>
                <Typography
                  size={theme.typography.h5}
                  color={theme.palette.wetAsphalt}
                  theme={theme}
                >
                  {mazdaCampaignMessage}
                </Typography>
              </MazdaCampaignMessage>
            )}
          </div>
        </div>
      </div>
    </WrapperPrivateIncentiveDetails>
  );
};

const MazdaCampaignMessage = styled.div`
  border-top: 1px solid ${theme.palette.backgroundGray};
  margin-top: 10px;
  overflow: auto;
`;

const CopyButton = styled.div`
  cursor: pointer;
  display: inline-block;
  text-transform: uppercase;
  border: 1px solid ${theme.palette.blue};
  padding: 4px 5px 3px;
  font-size: 12px;
  line-height: 1.2;
  color: ${theme.palette.blue};
  border-radius: 3px;
  white-space: nowrap;
  background-color: white;
  span {
    padding-right: 5px;
    vertical-align: bottom;
  }

  &:hover {
    color: ${theme.palette.darkGray};
    border: 1px solid ${theme.palette.darkGray};
  }
`;

type WrapperProps = {
  type?: string;
};

export const WrapperPrivateIncentiveDetails = styled.div<WrapperProps>`
  .private-inc-details {
    background-color: white;
    padding: ${(props) => (props.type == 'modal' ? '0px' : '20px')};
    border: ${(props) =>
      props.type == 'modal' || props.type == 'mobile' ? '0px solid #cccbca' : '1px solid #cccbca'};
    border-radius: 5px;
    margin: ${(props) => (props.type == 'mobile' ? '0px' : '20px')};
    width: ${(props) =>
      props.type == 'modal' ? '445px' : props.type == 'mobile' ? 'auto' : '490px'};
  }
  .header-text-details {
    text-align: center;
    padding-bottom: 50px;
    text-align: center;
    font-style: normal;
    font-weight: ${theme.typography.fontWeights.semibold};
    font-size: ${theme.typography.fontSize21}px;
    line-height: 21px;
  }
  .bold-sub-text {
    font-weight: ${theme.typography.fontWeights.bold};
    font-size: ${theme.typography.h4}px;
    line-height: 24px;
    margin-top: 12px;
  }
  .content-text {
    text-align: left;
    font-size: 16px;
  }
  .model-tag {
    display: flex;
    justify-content: space-between;
    line-height: 36px;
    font-size: ${theme.typography.h4}px;
  }

  .expiredSpan {
    color: white;
    justify-content: center;
    align-items: center;
    padding: 4px 10px 2px;
    gap: 10px;
    width: 55px;
    height: 14px;
    background: #c9112c;
    border-radius: 4px;
    font-weight: 600;
    font-size: 12px;
    padding-top: 2px;
    padding-bottom: 3px;
    margin-left: 5px;
  }

  .activeSpan {
    color: white;
    justify-content: center;
    align-items: center;
    padding: 4px 10px 2px;
    gap: 10px;
    width: 55px;
    height: 14px;
    background: #593487;
    border-radius: 4px;
    font-weight: 600;
    font-size: 12px;
    padding-top: 2px;
    padding-bottom: 3px;
    margin-left: 5px;
  }
  .form-group {
    padding-top: 2px;
    padding-bottom: 2px;
    margin-bottom: 15px;
  }
  hr.formatted {
    border: 1px solid #d3d3d3;
    margin-bottom: 0px;
    opacity: 1;
  }
  .no-margin {
    margin: 0px;
  }
  .excluded-model {
    overflow-wrap: break-word;
    margin-top: 10px;
  }
  .lboMsg {
    margin-bottom: 10px;
  }
  .primary-modal {
    overflow: hidden;
  }
  .end-primary-models {
    margin-bottom: 15px !important;
  }
  .program-name {
    font-size: 20px;
    font-weight: 500;
  }
`;

const ButtonWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.palette.lightGrey};
  border-radius: 3px;
  width: 84px;
  display: inline-block;
  float: right;
`;

type ButtonProps = {
  isActive?: boolean;
};

const Button = styled.button<ButtonProps>`
  background: ${(props) => (props.isActive ? props.theme.palette.white : props.theme.palette.gray)};
  border: none;
  width: 50%;
  height: 100%;

  .-icon {
    color: ${(props) =>
      props.isActive ? props.theme.palette.grayTwo : props.theme.palette.lightGreyTwo};
    vertical-align: text-top;
  }
`;

export default PrivateIncentiveDetails;
